/**
 * The bridge between how people write recipes and how the engine stores them.
 *
 * People write "serves 4, 500g mince". Every bundled meal stores 125g per ONE
 * adult portion. Nobody should ever type the second form, so the form takes
 * the first and this module divides. Pure — no I/O, no clock.
 */
import type {
  AisleCategory, BaseUnit, CostBand, ExtraItem, Ingredient, Meal, MealIngredient, MealTag, Unit,
} from './types.js';
import { round2 } from './units.js';

export type EntryUnit = 'g' | 'kg' | 'ml' | 'l' | 'tsp' | 'tbsp' | 'each';

/**
 * Only units in the ingredient's own dimension. Offering ml for mince would
 * save fine and then throw `UnitMismatchError` in the engine the first time the
 * week's list is built — a white screen in the shop.
 */
export const UNITS_FOR: Record<BaseUnit, EntryUnit[]> = {
  g: ['g', 'kg', 'tsp', 'tbsp'],
  ml: ['ml', 'l', 'tsp', 'tbsp'],
  each: ['each'],
};

/** Spoons by volume are exact. By mass they are a spice-and-paste approximation. */
const SPOON: Record<'g' | 'ml', Record<'tsp' | 'tbsp', number>> = {
  g: { tsp: 3, tbsp: 9 },
  ml: { tsp: 5, tbsp: 15 },
};

/**
 * What one "each" means. Garlic is counted in cloves and bread in slices; both
 * have shipped wrong before (a toddler's sandwich became two whole loaves).
 */
const EACH_LABEL: Record<string, string> = {
  garlic: 'cloves',
  'bread-white': 'slices',
  'bread-wholemeal': 'slices',
  celery: 'heads',
};

export function eachLabel(ingredientId: string): string {
  return EACH_LABEL[ingredientId] ?? 'each';
}

export function isApproximate(baseUnit: BaseUnit, unit: EntryUnit): boolean {
  return baseUnit === 'g' && (unit === 'tsp' || unit === 'tbsp');
}

/** A whole-recipe amount in an entry unit, as a quantity the engine can convert. */
export function toStoredAmount(amount: number, unit: EntryUnit, baseUnit: BaseUnit): { qty: number; unit: Unit } {
  if (!UNITS_FOR[baseUnit].includes(unit)) {
    throw new Error(`"${unit}" cannot measure an ingredient counted in ${baseUnit}.`);
  }
  if (unit === 'tsp' || unit === 'tbsp') {
    const base = baseUnit as 'g' | 'ml';
    return { qty: round2(amount * SPOON[base][unit]), unit: base };
  }
  return { qty: amount, unit };
}

export interface DraftIngredientLine {
  kind: 'ingredient';
  ingredientId: string;
  /** For the WHOLE recipe. Null until the person types it. */
  amount: number | null;
  unit: EntryUnit;
  prepNote: string | null;
  optional: boolean;
}

export interface DraftExtraLine {
  kind: 'extra';
  label: string;
  /** For the WHOLE recipe. Null means "some". */
  amount: number | null;
  unitLabel: string;
  aisle: AisleCategory;
}

export type DraftLine = DraftIngredientLine | DraftExtraLine;

export interface RecipeDraft {
  name: string;
  serves: number;
  lines: DraftLine[];
  method: string[];
  effortMinutes: number;
  cuisine: string;
  mainProtein: string;
  costBand: CostBand;
  tags: MealTag[];
  description: string;
}

/** Dropdowns, not free text: Build 2's like/dislike ranking keys on these. */
export const CUISINES: readonly string[] = [
  'british', 'italian', 'indian', 'mexican', 'chinese', 'thai', 'japanese',
  'spanish', 'greek', 'american', 'russian', 'other',
];

export const PROTEINS: readonly { value: string; label: string }[] = [
  { value: 'beef', label: 'Beef' },
  { value: 'chicken', label: 'Chicken' },
  { value: 'pork', label: 'Pork' },
  { value: 'lamb', label: 'Lamb' },
  { value: 'turkey', label: 'Turkey' },
  { value: 'fish', label: 'Fish' },
  { value: 'prawns', label: 'Prawns' },
  { value: 'egg', label: 'Egg' },
  { value: 'none', label: 'No meat or fish' },
  { value: 'other', label: 'Something else' },
];

export const ALL_TAGS: readonly MealTag[] = ['budget', 'batch', 'quick', 'veggie', 'mild', 'freezer'];

export function emptyDraft(): RecipeDraft {
  return {
    name: '', serves: 4, lines: [], method: [], effortMinutes: 30,
    cuisine: 'other', mainProtein: 'other', costBand: 'mid', tags: [], description: '',
  };
}

export function isBlankDraft(draft: RecipeDraft): boolean {
  return draft.name.trim() === '' && draft.lines.length === 0;
}

const validAmount = (n: number | null): n is number => n !== null && Number.isFinite(n) && n > 0;

/** Everything stopping this draft being saved, in the order a person would fix it. */
export function draftProblems(draft: RecipeDraft, ingredients: ReadonlyMap<string, Ingredient>): string[] {
  const problems: string[] = [];
  if (draft.name.trim() === '') problems.push('Give the recipe a name.');
  if (!Number.isInteger(draft.serves) || draft.serves < 1 || draft.serves > 20) {
    problems.push('Serves must be a whole number from 1 to 20.');
  }
  if (draft.lines.length === 0) problems.push('Add at least one ingredient.');

  for (const line of draft.lines) {
    if (line.kind === 'ingredient') {
      const ingredient = ingredients.get(line.ingredientId);
      if (!ingredient) {
        problems.push(`"${line.ingredientId}" is not in the ingredient list.`);
        continue;
      }
      if (!validAmount(line.amount)) problems.push(`How much ${ingredient.name.toLowerCase()}?`);
      else if (!UNITS_FOR[ingredient.baseUnit].includes(line.unit)) {
        problems.push(`${ingredient.name} can't be measured in ${line.unit}.`);
      }
    } else {
      if (line.label.trim() === '') problems.push('An extra item needs a name.');
      else if (line.amount !== null && !validAmount(line.amount)) {
        problems.push(`How much ${line.label.trim().toLowerCase()}?`);
      }
    }
  }
  return problems;
}

/** A saved meal. Throws if the draft has problems — the form checks first. */
export function draftToMeal(
  draft: RecipeDraft,
  ingredients: ReadonlyMap<string, Ingredient>,
  id: string,
): Meal {
  const problems = draftProblems(draft, ingredients);
  if (problems.length > 0) throw new Error(`Recipe is not ready to save: ${problems.join(' ')}`);

  const mealIngredients: MealIngredient[] = [];
  const extras: ExtraItem[] = [];
  for (const line of draft.lines) {
    if (line.kind === 'ingredient') {
      const ingredient = ingredients.get(line.ingredientId) as Ingredient;
      const stored = toStoredAmount(line.amount as number, line.unit, ingredient.baseUnit);
      mealIngredients.push({
        ingredientId: ingredient.id,
        qtyPerPortion: round2(stored.qty / draft.serves),
        unit: stored.unit,
        isOptional: line.optional,
        prepNote: line.prepNote,
      });
    } else {
      extras.push({
        label: line.label.trim(),
        qtyPerPortion: line.amount === null ? null : round2(line.amount / draft.serves),
        unitLabel: line.amount === null ? '' : line.unitLabel.trim(),
        aisle: line.aisle,
      });
    }
  }

  return {
    id,
    name: draft.name.trim(),
    description: draft.description.trim(),
    cuisine: draft.cuisine,
    mainProtein: draft.mainProtein,
    effortMinutes: draft.effortMinutes,
    costBand: draft.costBand,
    tags: [...draft.tags],
    ingredients: mealIngredients,
    extras,
    method: draft.method.map((s) => s.trim()).filter((s) => s.length > 0),
  };
}

/** Re-open a saved meal in the form, amounts scaled back up to the whole recipe. */
export function mealToDraft(meal: Meal, serves: number): RecipeDraft {
  return {
    name: meal.name,
    serves,
    description: meal.description,
    cuisine: meal.cuisine,
    mainProtein: meal.mainProtein,
    effortMinutes: meal.effortMinutes,
    costBand: meal.costBand,
    tags: [...meal.tags],
    method: [...meal.method],
    lines: [
      ...meal.ingredients.map((i): DraftLine => ({
        kind: 'ingredient', ingredientId: i.ingredientId, amount: round2(i.qtyPerPortion * serves),
        unit: i.unit, prepNote: i.prepNote, optional: i.isOptional,
      })),
      ...(meal.extras ?? []).map((e): DraftLine => ({
        kind: 'extra', label: e.label,
        amount: e.qtyPerPortion === null ? null : round2(e.qtyPerPortion * serves),
        unitLabel: e.unitLabel, aisle: e.aisle,
      })),
    ],
  };
}
